import type { MailArchiveItem, MailArchiveStatus } from "../../services/mailArchiveService";

/**
 * Reine, DOM-freie Ableitungslogik der Ablage-Ansicht (Cluster ABLAGE, M3).
 * Muster: `mailFilter.ts`, `mailContactAssign.ts` — unter Vitest ohne Rendering
 * prüfbar.
 */

/** Die vier Filterpillen der Ansicht. */
export type MailArchiveFilter = "ohneKontakt" | "fehlgeschlagen" | "uebersprungen" | "alle";

export const MAIL_ARCHIVE_FILTER: ReadonlyArray<{ key: MailArchiveFilter; label: string }> = [
    { key: "ohneKontakt", label: "Ohne Kontakt" },
    { key: "fehlgeschlagen", label: "Fehlgeschlagen" },
    // E11: eigene Pille — übersprungene (zu große) Mails dürfen nicht in
    // „Fehlgeschlagen" untergehen, sie sind ein anderer Fall.
    { key: "uebersprungen", label: "Übersprungen" },
    { key: "alle", label: "Alle" },
];

export function statusLabel(status: MailArchiveStatus): string {
    switch (status) {
        case "stored":
            return "Abgelegt";
        case "pending":
            return "Wird abgelegt";
        case "failed":
            return "Fehlgeschlagen";
        case "skipped":
            return "Übersprungen";
        case "purged":
            return "Gelöscht";
        default:
            return status;
    }
}

/** Farbe der Status-Pille — bewusst warm statt rot/grün-Ampel. */
export function statusFarbe(status: MailArchiveStatus): { bg: string; fg: string } {
    switch (status) {
        case "stored":
            return { bg: "#eef6ef", fg: "#2f6b3a" };
        case "pending":
            return { bg: "#f4f1ee", fg: "#6b5a66" };
        case "failed":
            return { bg: "#fdf3f4", fg: "#a4262c" };
        case "skipped":
            return { bg: "#fbf1e3", fg: "#8a5b12" };
        default:
            return { bg: "#f1eef1", fg: "#6b5a66" };
    }
}

/** Prädikat einer Filterpille. */
export function passtZuFilter(item: MailArchiveItem, filter: MailArchiveFilter): boolean {
    switch (filter) {
        case "ohneKontakt":
            return item.contact === null && item.status !== "purged";
        case "fehlgeschlagen":
            return item.status === "failed";
        case "uebersprungen":
            return item.status === "skipped";
        case "alle":
        default:
            return item.status !== "purged";
    }
}

/** Ist die Zeile erneut versuchbar? `skipped` ausdrücklich JA (E11). */
export function istWiederholbar(item: MailArchiveItem): boolean {
    return item.status === "failed" || item.status === "skipped";
}

export type MailArchiveGruppe = { label: string; items: MailArchiveItem[] };

const TAG_MS = 24 * 60 * 60 * 1000;

function tagesLabel(iso: string | null): string {
    if (!iso) return "Ohne Datum";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "Ohne Datum";
    const heute = new Date();
    const tagesStart = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    const diff = tagesStart(heute) - tagesStart(d);
    if (diff === 0) return "Heute";
    if (diff === TAG_MS) return "Gestern";
    return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

/** Nach Tag gruppieren, neueste zuerst — Reihenfolge der Eingabe bleibt erhalten. */
export function gruppiereNachTag(items: MailArchiveItem[]): MailArchiveGruppe[] {
    const out: MailArchiveGruppe[] = [];
    for (const item of items) {
        const label = tagesLabel(item.receivedAt ?? item.createdAt);
        const letzte = out[out.length - 1];
        if (letzte && letzte.label === label) letzte.items.push(item);
        else out.push({ label, items: [item] });
    }
    return out;
}

/** Lesbare Größe für die Zeile („1,2 MB"). */
export function groesseLabel(bytes: number | null): string {
    if (bytes == null || bytes <= 0) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} MB`;
}
